import { REJECT, array, nullable, num, object, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

/**
 * A single data table as returned by nap-backend
 * `GET /tables/:zone_name/:table_name` (routes/tables.js). Re-derived from the
 * backend SELECT, NOT the `tablesSlice` `TableItem` type, which drifted:
 * `table_privileges` was dropped from the table entirely (migration 0244) and
 * is neither returned here nor consumed by the editor, and `table_columns` is a
 * nullable jsonb column. The endpoint also returns `folder_path`, which the
 * editor does not read, so it is dropped by the object parser. `table_columns`
 * is validated to the `{ name, type }` shape the editor uses (it rewrites the
 * whole column set on save). Deeply readonly — validated wire data.
 */
export interface FetchedTable {
  readonly table_id: number;
  readonly table_name: string;
  readonly table_columns: { readonly name: string; readonly type: string }[] | null;
  readonly zone_id: number;
  readonly folder_id: number | null;
  readonly zone_name: string;
}

const tableSchema = object<FetchedTable>({
  table_id: num(REJECT),
  table_name: str(),
  table_columns: nullable(array(object<{ name: string; type: string }>({ name: str(), type: str() }))),
  zone_id: num(),
  folder_id: nullable(num()),
  zone_name: str(),
});

/**
 * GET `/tables/:id` — one record for the edit dialog, so it works
 * when launched from the Workspace (which never populates the list slice) and
 * never opens on a loosely-cast store row.
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such record, and
 * the failure arm means it said something we could not read. The caller must
 * tell those apart, because refreshing a list helps with only one of them.
 */
export const getTable = (id: number): Promise<Received<FetchedTable | null>> => getRecord(`/tables/${id}`, { schema: tableSchema, source: 'getTable' });
